/**
 * WebMCP plumbing (projects/webmcp W1, W2): find the browser's model
 * context, register tools on it, and unregister them all with one signal.
 *
 * Feature-detected, never required: without WebMCP this is a no-op, and a
 * tool that fails to register is a console warning — never a broken page.
 */

/** The page's model context: `document.modelContext` (the current draft),
 * else `navigator.modelContext` (Chrome's 149 trial builds), else null. */
export function getModelContext(
  doc = globalThis.document,
  nav = globalThis.navigator,
) {
  const context = doc?.modelContext ?? nav?.modelContext ?? null;
  return context && typeof context.registerTool === "function" ? context : null;
}

const warn = (name, error) =>
  console.warn(`WebMCP: couldn't register "${name}":`, error);

/**
 * Register `tools`; aborting `signal` unregisters every one. The draft
 * unregisters on abort itself (and rejects the registration with the abort
 * reason — expected, so not a warning); the early API had no signal, so
 * `unregisterTool(name)` is called too where it exists.
 */
export function registerTools(context, tools, signal) {
  for (const tool of tools) {
    let result;
    try {
      result = context.registerTool(tool, { signal });
    } catch (error) {
      warn(tool.name, error);
      continue;
    }
    Promise.resolve(result).catch((error) => {
      if (!signal.aborted) warn(tool.name, error);
    });
    if (typeof context.unregisterTool === "function") {
      signal.addEventListener(
        "abort",
        () => {
          try {
            context.unregisterTool(tool.name);
          } catch {
            // already gone (the draft unregistered it on abort)
          }
        },
        { once: true },
      );
    }
  }
}
